/**
 * `⌘K` / `Ctrl+K` — o atalho que abre e fecha a paleta de busca (AC-5).
 *
 * ONDE VIVE, E POR QUÊ AQUI: o listener é registado pelo `AppLayout`, o único
 * componente presente em TODAS as rotas autenticadas. Pendurá-lo no diálogo não
 * serviria — o diálogo só existe depois de aberto, e é justamente abri-lo que o
 * atalho faz.
 *
 * ⚠️ FASE DE CAPTURA COM `preventDefault`, e as duas coisas são precisas.
 * `⌘K`/`Ctrl+K` já está tomado em vários browsers (foca a barra de endereço no
 * Chrome e no Firefox). Sem `preventDefault`, a paleta abre E o browser leva o
 * foco para fora da página — a pessoa escreve na barra de endereço a pensar que
 * escreve na busca. A captura garante que chegamos antes de qualquer handler de
 * um campo que esteja focado; o `preventDefault` é o que trava o browser.
 */
import { useEffect } from 'react'
import { useGlobalSearchStore } from '@/stores/global-search-store'

/**
 * A plataforma é lida do `navigator`, e a leitura é uma FUNÇÃO e não uma
 * constante de módulo: uma constante seria avaliada uma vez no import e não
 * haveria como provar os dois ramos sem recarregar o módulo inteiro.
 */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const plataforma = navigator.platform || navigator.userAgent || ''
  return /Mac|iPhone|iPad|iPod/i.test(plataforma)
}

/**
 * A etiqueta da cápsula — `⌘K` no macOS, `Ctrl+K` no resto.
 *
 * Uma etiqueta de atalho que MENTE é pior que a ausência dela: ensina o gesto
 * errado, e quem o tenta conclui que a funcionalidade está partida.
 */
export function searchShortcutLabel(mac: boolean = isMacPlatform()): string {
  return mac ? '⌘K' : 'Ctrl+K'
}

/** O `aria-keyshortcuts` cobre as duas plataformas — quem lê é o leitor de ecrã. */
export const SEARCH_SHORTCUT_ARIA = 'Meta+K Control+K'

export function useGlobalSearchHotkey(): void {
  const toggle = useGlobalSearchStore((s) => s.toggle)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.altKey) return
      if (event.key.toLowerCase() !== 'k') return
      // Ver o cabeçalho: sem isto o browser rouba o foco e a busca abre atrás.
      event.preventDefault()
      // Com um painel lateral aberto (`ui/sheet.tsx`), o atalho não faz nada. O
      // painel é um diálogo modal do Radix: devolve-se o foco a ele e o resto da
      // página fica `aria-hidden` — a paleta abriria muda e sem foco. Fechá-lo
      // para abrir a busca, como a gaveta do celular faz, deitaria fora um
      // formulário a meio. O `preventDefault` fica ANTES, de propósito.
      if (document.querySelector('[data-painel-lateral]')) return
      toggle()
    }

    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [toggle])
}
